import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

import {
  heartbeat as apiHeartbeat,
  login as apiLogin,
  logout as apiLogout,
  me as apiMe,
  type LogoutReason,
  type Role,
} from "@/api/auth";
import {
  registerUnauthorizedHandler,
  setBearerFallback,
} from "@/api/client";

type Me = { username: string; role: Role; must_change_password: boolean };

type AuthCtx = {
  me: Me | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: (reason?: LogoutReason) => Promise<void>;
  /** Called by the change-password page after a successful self-change so
   * downstream pages stop redirecting the user back to the forced flow. */
  markPasswordChanged: () => void;
};

const Ctx = createContext<AuthCtx | null>(null);

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 min
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 60 s
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
];

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      setMe(null);
      setBearerFallback(null);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiMe();
        if (!cancelled) {
          setMe(
            r.username && r.role
              ? {
                  username: r.username,
                  role: r.role,
                  must_change_password: r.must_change_password,
                }
              : null,
          );
        }
      } catch {
        if (!cancelled) setMe(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await apiLogin(username, password);
    const probe = await apiMe();
    if (probe.username && probe.role) {
      setMe({
        username: probe.username,
        role: probe.role,
        must_change_password: probe.must_change_password,
      });
      setBearerFallback(null);
    } else {
      setBearerFallback(res.access_token);
      setMe({
        username: res.username,
        role: res.role,
        must_change_password: res.must_change_password,
      });
    }
  }, []);

  const logout = useCallback(
    async (reason: LogoutReason = "manual") => {
      try {
        await apiLogout(reason);
      } catch {
        /* server cookie may already be gone; ignore */
      }
      setMe(null);
      setBearerFallback(null);
      navigate("/admin/login", { replace: true });
    },
    [navigate],
  );

  // Keep the freshest logout in a ref so the idle/heartbeat effect below can
  // reference it without re-subscribing every render.
  const logoutRef = useRef(logout);
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Idle detector + heartbeat. Only wired when there IS a logged-in user, so
  // we don't attach listeners on the /login screen.
  useEffect(() => {
    if (!me) return;

    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let disposed = false;

    const triggerAutoLogout = () => {
      if (disposed) return;
      // Fire and forget; logout() handles its own errors.
      void logoutRef.current("inactivity");
    };

    const armIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(triggerAutoLogout, IDLE_TIMEOUT_MS);
    };

    // Throttle: at most one reset per second, so mousemove doesn't burn CPU
    // clearing/setting timeouts on every pixel.
    let lastActivityMs = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityMs < 1000) return;
      lastActivityMs = now;
      armIdleTimer();
    };

    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true, capture: true });
    }
    armIdleTimer();

    // Heartbeat every 60s. Fire once immediately so the DB row's
    // last_heartbeat_at is fresh even for short sessions.
    const pingHeartbeat = () => {
      void apiHeartbeat().catch(() => {
        /* transient network issues are fine – sweeper's grace window covers
         * up to 15 min of missed heartbeats. */
      });
    };
    pingHeartbeat();
    heartbeatTimer = setInterval(pingHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      disposed = true;
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity, { capture: true } as EventListenerOptions);
      }
      if (idleTimer) clearTimeout(idleTimer);
      if (heartbeatTimer) clearInterval(heartbeatTimer);
    };
  }, [me]);

  const markPasswordChanged = useCallback(() => {
    setMe((prev) => (prev ? { ...prev, must_change_password: false } : prev));
  }, []);

  const value = useMemo(
    () => ({ me, loading, login, logout, markPasswordChanged }),
    [me, loading, login, logout, markPasswordChanged],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}
